<template>
  <div class="quote-result">
    <v-alert v-if="warnings.includes('EXCHANGE_RATE_STALE')" type="warning" dense text class="mb-3">
      El tipo de cambio tiene más de 30 días.
    </v-alert>

    <div v-if="showMeta" class="d-flex flex-wrap align-center mb-3 quote-result__meta">
      <span v-if="commissionPct" class="pw-label mr-2">Comisión</span>
      <span v-if="commissionPct" class="pw-value mr-4">{{ pct(commissionPct) }}</span>
      <span class="pw-label mr-2">Moneda</span>
      <span class="pw-value">{{ currency }}</span>
    </div>

    <v-row>
      <v-col v-for="(line, index) in lines" :key="index" cols="12" sm="6" :md="columnWidth">
        <v-card outlined class="quote-result__plan" :class="{ 'quote-result__plan--custom': isCustom(line) }">
          <v-card-title class="py-2 text-subtitle-1 font-weight-bold">{{ line.label }}</v-card-title>
          <v-card-text>
            <div class="pw-kpi">{{ money(line.monthlyTotal) }}</div>

            <div class="pw-value mt-2">
              {{ money(isAssistance ? line.perInsured : line.netAfterDiscount) }}
              <span class="text-caption">{{ priceUnit }}</span>
            </div>

            <div v-if="line.minimumApplied" class="text-caption mt-1">
              Total ajustado al mínimo mensual ({{ minimumUsd }})
            </div>

            <div v-if="extraPetPriceOf(line)" class="quote-result__extra mt-3">
              <div class="text-caption">{{ extraPetLabel }}</div>
              <div class="pw-value">
                {{ money(extraPetPriceOf(line)) }}
                <span class="text-caption">al mes</span>
              </div>
            </div>

            <div v-if="referenceOf(line)" class="quote-result__market mt-3">
              <div class="text-caption">
                {{ versusMedian(line) }} ·
                <a class="quote-result__link" @click="shownReference = referenceOf(line)">
                  ver {{ referenceOf(line).contracts.length }} contratos
                </a>
              </div>
            </div>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog :value="Boolean(shownReference)" max-width="900" @input="shownReference = null">
      <v-card v-if="shownReference">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Contratos activos{{ isAssistance ? ' de Asistencias' : '' }} con planes tipo {{ kindLabel(shownReference.kind) }}
        </v-card-title>
        <v-card-text>
          <v-simple-table dense class="quote-result__table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th v-if="isAssistance">Producto</th>
                <th>Plan</th>
                <th class="text-right">Precio</th>
                <th>Cobro</th>
                <th>Canal</th>
                <th class="text-right">{{ isAssistance ? 'Asegurados' : 'Colaboradores' }}</th>
                <th class="text-right">Mascotas activas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in shownReference.contracts" :key="`${c.contratoId}-${c.plan}`">
                <td>{{ c.company }}</td>
                <td v-if="isAssistance">{{ c.product || '—' }}</td>
                <td>{{ c.plan }}</td>
                <td class="text-right">{{ contractPrice(c) }}</td>
                <td>{{ c.unit === 'membership' ? 'por mascota' : isAssistance ? 'por asegurado' : 'por colaborador' }}</td>
                <td>{{ c.channel || '—' }}</td>
                <td class="text-right">{{ count(c.employees) }}</td>
                <td class="text-right">{{ count(c.activePets) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption mt-2 mb-0">
            Precio neto mensual de cada contrato, sin el cupo mínimo. Tipo Urgencias si cubre urgencias; tipo Wellness si tiene al menos 3 servicios presenciales (los planes de solo chip y antirrábica no cuentan). Úsalo
            para negociar: el precio del cotizador es el piso recomendado y puedes descontar si la negociación lo pide.
            <span v-if="isAssistance">
              En Asistencias se comparan contratos con el mismo tipo de producto (de mascotas o no), y los precios en UF se
              pasan a pesos con la UF de hoy.
            </span>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="shownReference = null">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatMoney, formatPct } from '../utils/b2b-quotes.utils';

/**
 * Plans of a quote side by side, kept to what the executive offers: price and
 * monthly total, and what active clients pay for similar plans. They are alternatives, so there is no grand total. Discounts
 * need no approval, so there is no margin warning.
 */
export default {
  name: 'QuoteResult',
  props: {
    lines: { type: Array, default: () => [] },
    currency: { type: String, default: 'CLP' },
    commissionPct: { type: Number, default: 0 },
    warnings: { type: Array, default: () => [] },
    isAssistance: { type: Boolean, default: false },
    /** UF in pesos: when set, the plans' amounts show in UF (Asistencias in Chile). */
    ufValue: { type: Number, default: null },
    /** Monthly minimum of a plan, worded in USD ("USD 99"). */
    minimumUsd: { type: String, default: '' },

    /** Tier, commission and currency line; off when the parent shows its own headers. */
    showMeta: { type: Boolean, default: true },
    /** What active clients pay for Wellness- and Urgencias-like plans (Employee Benefit only). */
    marketReferences: { type: Array, default: () => [] }
  },
  data() {
    return { shownReference: null };
  },
  computed: {
    columnWidth() {
      return Math.max(3, Math.floor(12 / Math.max(this.lines.length, 1)));
    },
    priceUnit() {
      return this.isAssistance ? 'por asegurado' : 'por membresía';
    },
    // In Asistencias the insured person pays for the extra pet (pawer-backend spec 122).
    extraPetLabel() {
      return this.isAssistance
        ? 'Mascota adicional pagada por el asegurado'
        : 'Mascota adicional pagada por el colaborador';
    }
  },
  methods: {
    /** Price of an extra pet the employee (Employee Benefit) or the insured person (Asistencias) pays on their own. */
    extraPetPriceOf(line) {
      return line.employeeExtras?.extraPetPrice ?? null;
    },
    money(amount, currency) {
      if (this.ufValue && !currency && amount !== null && amount !== undefined) {
        const uf = amount / this.ufValue;
        // A price per insured is a fraction of a UF: four decimals keep it readable.
        const decimals = Math.abs(uf) < 1 ? 4 : 2;
        return `UF ${uf.toLocaleString('es-CL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
      }
      return formatMoney(amount, currency || this.currency);
    },
    count(value) {
      return Math.round(value).toLocaleString('es-CL');
    },
    pct(value, decimals) {
      return formatPct(value, decimals);
    },
    referenceOf(line) {
      // Pro has no emergency: it compares with the Wellness-like plans (pawer-backend spec 123).
      const kind = line.basePlan === 'pro' ? 'wellness' : line.basePlan;
      const reference = this.marketReferences.find((r) => r.kind === kind);
      return reference && reference.median !== null ? reference : null;
    },
    /** "$1.463 menos que la mediana (−49%)": the gap in money first, the percentage of the median after. */
    versusMedian(line) {
      const median = this.referenceOf(line).median;
      const price = this.isAssistance ? line.perInsured : line.netAfterDiscount;
      const gap = price - median;
      const peers = this.isAssistance ? 'contratos de Asistencias parecidos' : 'clientes con planes parecidos';
      if (Math.abs(gap) < median * 0.05) return `Este precio está en la mediana de ${peers}`;
      const sign = gap < 0 ? '−' : '+';
      return `Este precio es ${this.money(Math.abs(gap))} ${gap < 0 ? 'menos' : 'más'} que la mediana de ${peers} (${sign}${this.pct(Math.abs(gap) / median)})`;
    },
    /** A contract billed in UF shows its UF price and what it is in pesos today. */
    contractPrice(contract) {
      const pesos = formatMoney(contract.price, contract.currency);
      if (contract.originalCurrency !== 'UF') return pesos;
      const uf = contract.originalPrice.toLocaleString('es-CL', { maximumFractionDigits: 4 });
      return `UF ${uf} (${pesos})`;
    },
    kindLabel(kind) {
      return kind === 'urgencias' ? 'Urgencias' : 'Wellness';
    },
    /** A plan a medida: it changes services or starts from a broker variant. */
    isCustom(line) {
      return line.isCustom || Boolean(line.partnerVariantId);
    }

  }
};
</script>

<style scoped>
.quote-result__meta {
  gap: 4px;
}
.quote-result__plan {
  height: 100%;
}
.quote-result__extra,
.quote-result__market {
  border-top: 1px solid var(--pw-border);
  padding-top: 8px;
  line-height: 1.35;
}
.quote-result__link {
  cursor: pointer;
  text-decoration: underline;
}
.quote-result__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
.quote-result__plan--custom {
  background: var(--pw-info-tint);
}
</style>
